import React, { useState, useEffect } from 'react';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faIdCard, faGlobe, faHandshake, faEnvelope, faShieldHalved } from "@fortawesome/free-solid-svg-icons";

import { Checkbox } from '@/components/form/Choice.jsx';
import { PrimaryButton, SecondaryButton } from "../../../../components/button/Button.jsx";
import { getOrgById } from '../../../../services/admin-settings/org-management/OrgRegistrationService.jsx';
import { getAllCurrencies, getAllTimezones, getAllCountries } from "../../../../services/players/player-attributes/GeoDataStore";
import { UserAuth } from '../../../../authentication/AuthContext.jsx';
import { formatEnumValue } from '@/utils/formatEnumValue.js';

import './AccessManagementOrgRegistration.css';

const AccessManagementOrgRegistration = ({ onAccept, onDecline, invitation = {}, acceptLoading, declineLoading }) => {

    const { user } = UserAuth();

    const [acknowledged, setAcknowledged] = useState(false);
    const [orgData, setOrgData] = useState(null);
    const [countryData, setCountryData] = useState([]);
    const [timezoneData, setTimezoneData] = useState([]);
    const [currencyData, setCurrencyData] = useState([]);

    useEffect(() => {
        const fetchOrgData = async () => {
            const orgId = invitation?.orgDetails?.orgId;
            if (!orgId) return;
            try {
                const data = await getOrgById({
                    accessToken: user?.accessToken,
                    id: orgId,
                });
                setOrgData(data?.org);
            } catch (err) {
                console.error('Failed to fetch org data:', err);
            }
        };

        fetchOrgData();
    }, [invitation?.orgDetails?.orgId, user?.accessToken]);

    useEffect(() => {
        const fetchGeoData = async () => {
            try {
                const [countriesResponse, timezoneResponse, currenciesResponse] = await Promise.all([
                    getAllCountries(),
                    getAllTimezones(),
                    getAllCurrencies(),
                ]);
                setCountryData(countriesResponse?.countries || []);
                setTimezoneData(timezoneResponse?.timezones || []);
                setCurrencyData(currenciesResponse?.currencies || []);
            } catch (err) {
                console.error('Failed to fetch geo data:', err);
            }
        };

        fetchGeoData();
    }, []);

    const orgOwner = orgData?.orgOwner || '—';
    const orgName = orgData?.orgName || '—';
    const orgType = orgData?.orgType || '—';

    const operatingCountry = countryData.find(c => c.code === orgData?.operatingCountry)?.displayName || orgData?.operatingCountry || '—';
    const timeZone = timezoneData.find(t => t.code === orgData?.timeZone)?.displayName || orgData?.timeZone || '—';
    const currency = currencyData.find(c => c.code === orgData?.currency)?.displayName || orgData?.currency || '—';

    const operatingRegions = Array.isArray(orgData?.operatingRegions)
        ? orgData.operatingRegions.map((r) => (typeof r === 'string' ? r : r?.name || r?.regionName || JSON.stringify(r)))
        : [];

    const engagementTypes = Array.isArray(orgData?.engagementTypes)
        ? orgData.engagementTypes.map((e) => (typeof e === 'string' ? e : e?.type || e?.name || JSON.stringify(e)))
        : [];

    const internalTeams = Array.isArray(orgData?.internalTeams)
        ? orgData.internalTeams.map((t) => (typeof t === 'string' ? t : t?.teamName || t?.name || JSON.stringify(t)))
        : [];

    const coOwners = Array.isArray(orgData?.coOwners) ? orgData.coOwners : [];
    const allowedDomains = Array.isArray(orgData?.allowedDomains) ? orgData.allowedDomains : [];

    return (
        <div className="org-access-mgmt-card">
            {/* ===== Header ===== */}
            <div className="org-access-mgmt-header">
                <div className="org-access-mgmt-header-icon-wrapper">
                    <FontAwesomeIcon icon={faBuilding} />
                </div>
                <div className="org-access-mgmt-header-title-section">
                    <h2 className="org-access-mgmt-header-title">Accept Invitation</h2>
                    <p className="org-access-mgmt-header-subtitle">
                        Accept the invite to join and access assigned tools and projects.
                    </p>
                </div>
            </div>

            {/* ===== Scrollable Body ===== */}
            <div className="org-access-mgmt-body">
                {/* Section 1: Identity & Communication Hub */}
                <div className="org-access-mgmt-section">
                    <h3 className="org-access-mgmt-section-title">
                        <FontAwesomeIcon icon={faIdCard} />
                        Identity & Communication Hub
                    </h3>
                    <div className="org-access-mgmt-fields-grid">
                        <div className="org-access-mgmt-field">
                            <span className="org-access-mgmt-field-label">User Profile</span>
                            <span className="org-access-mgmt-field-value">{orgOwner}</span>
                        </div>
                        <div className="org-access-mgmt-field">
                            <span className="org-access-mgmt-field-label">Organization Name</span>
                            <span className="org-access-mgmt-field-value">{orgName}</span>
                        </div>
                        <div className="org-access-mgmt-field">
                            <span className="org-access-mgmt-field-label">Role</span>
                            <span className="org-access-mgmt-field-value">Org Owner</span>
                        </div>
                        <div className="org-access-mgmt-field">
                            <span className="org-access-mgmt-field-label">Organization Type</span>
                            <span className="org-access-mgmt-field-value">{formatEnumValue(orgType)}</span>
                        </div>
                        {coOwners.length > 0 && (
                            <div className="org-access-mgmt-field">
                                <span className="org-access-mgmt-field-label">Co-Owners</span>
                                <span className="org-access-mgmt-field-value">{coOwners.join(', ')}</span>
                            </div>
                        )}
                    </div>
                </div>

                <hr />

                {/* Section 2: Regional & Operational Context */}
                <div className="org-access-mgmt-section">
                    <h3 className="org-access-mgmt-section-title">
                        <FontAwesomeIcon icon={faGlobe} />
                        Regional & Operational Context
                    </h3>
                    <div className="org-access-mgmt-fields-grid">
                        <div className="org-access-mgmt-field">
                            <span className="org-access-mgmt-field-label">Operating Country</span>
                            <span className="org-access-mgmt-field-value">{operatingCountry}</span>
                        </div>
                        <div className="org-access-mgmt-field">
                            <span className="org-access-mgmt-field-label">Time Zone</span>
                            <span className="org-access-mgmt-field-value">{timeZone}</span>
                        </div>
                        <div className="org-access-mgmt-field">
                            <span className="org-access-mgmt-field-label">Currency</span>
                            <span className="org-access-mgmt-field-value">{currency}</span>
                        </div>
                    </div>
                    {operatingRegions.length > 0 && (
                        <div className="org-access-mgmt-field">
                            <span className="org-access-mgmt-field-label">Operating Regions</span>
                            <div className="org-access-mgmt-tags-section">
                                {operatingRegions.map((region, index) => (
                                    <span key={index} className="org-access-mgmt-tag">{region}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <hr />

                {/* Section 3: Engagement & Internal Governance */}
                <div className="org-access-mgmt-section">
                    <h3 className="org-access-mgmt-section-title">
                        <FontAwesomeIcon icon={faHandshake} />
                        Engagement & Internal Governance
                    </h3>
                    <div className="org-access-mgmt-fields-grid">
                        {engagementTypes.length > 0 && (
                            <div className="org-access-mgmt-field">
                                <span className="org-access-mgmt-field-label">Engagement Types</span>
                                <div className="org-access-mgmt-tags-section">
                                    {engagementTypes.map((type, index) => (
                                        <span key={index} className="org-access-mgmt-tag">{formatEnumValue(type)}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {internalTeams.length > 0 && (
                            <div className="org-access-mgmt-field">
                                <span className="org-access-mgmt-field-label">Internal Teams</span>
                                <div className="org-access-mgmt-tags-section">
                                    {internalTeams.map((team, index) => (
                                        <span key={index} className="org-access-mgmt-tag">{formatEnumValue(team)}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {allowedDomains.length > 0 && (
                    <>
                        <hr />

                        {/* Section 4: Allowed Domains */}
                        <div className="org-access-mgmt-section">
                            <h3 className="org-access-mgmt-section-title">
                                <FontAwesomeIcon icon={faEnvelope} />
                                Allowed Domains
                            </h3>
                            <div className="org-access-mgmt-tags-section">
                                {allowedDomains.map((domain, index) => (
                                    <span key={index} className="org-access-mgmt-tag">{domain}</span>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                <hr />

                {/* Section 5: Compliance */}
                <div className="org-access-mgmt-section">
                    <h3 className="org-access-mgmt-section-title">
                        <FontAwesomeIcon icon={faShieldHalved} />
                        External Entity Compliance
                    </h3>
                    <Checkbox
                        label="I Acknowledge and accept workspace policies"
                        name="t&c"
                        value="t&c"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                    />
                </div>
            </div>

            {/* ===== Footer ===== */}
            <div className="org-access-mgmt-footer">
                <SecondaryButton onClick={onDecline} disabled={declineLoading || acceptLoading}>
                    {declineLoading ? 'Declining...' : 'Decline'}
                </SecondaryButton>
                <PrimaryButton
                    onClick={onAccept}
                    disabled={!acknowledged || acceptLoading || declineLoading}
                >
                    {acceptLoading ? 'Accepting...' : 'Accept'}
                </PrimaryButton>
            </div>
        </div>
    );
};

export default AccessManagementOrgRegistration;